import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Answer } from '../../types/answer';
import { DataTableFilterMeta } from 'primereact/datatable';
import { defaultAnswerFilters } from '../../utils/answerTableHelper';
import { TablePageState } from '../../types/table';
import { Inspection } from '../../types/inspection';

export interface AnswerState {
    inspections: Inspection[];
    loadingInspections: boolean;
    errorInspections: boolean;
    selectedInspection: Inspection | undefined;
    data: Answer[];
    loading: boolean;
    error: boolean;
    globalFilterValue: string;
    filters: DataTableFilterMeta;
    page: TablePageState;
    toEdit: Answer | undefined;
    failed: string[];
    filteredAnswers: Answer[];
}

export const initialState: AnswerState = {
    inspections: [],
    loadingInspections: true,
    errorInspections: false,
    selectedInspection: undefined,
    data: [],
    loading: false,
    error: false,
    globalFilterValue: "",
    filters: defaultAnswerFilters,
    page: { row: 50, first: 0 },
    toEdit: undefined,
    failed: [],
    filteredAnswers: [],
};

export const answerSlice = createSlice({
    name: 'answer',
    initialState,
    reducers: {
        setInspections: (state, action: PayloadAction<Inspection[]>) => {
            state.inspections = action.payload;
        },
        setLoadingInspections: (state, action: PayloadAction<boolean>) => {
            state.loadingInspections = action.payload;
        },
        setErrorInspections: (state, action: PayloadAction<boolean>) => {
            state.errorInspections = action.payload;
        },
        setSelectedInspection: (state, action: PayloadAction<Inspection | undefined>) => {
            state.selectedInspection = action.payload;
        },
        setData: (state, action: PayloadAction<Answer[]>) => {
            state.data = action.payload;
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },
        setError: (state, action: PayloadAction<boolean>) => {
            state.error = action.payload;
        },
        setGlobalFilterValue: (state, action: PayloadAction<string>) => {
            state.globalFilterValue = action.payload;
            // Sync with filters.global for DataTable filtering
            if (state.filters.global && typeof state.filters.global === 'object' && 'value' in state.filters.global) {
                state.filters.global.value = action.payload || null;
            }
        },
        setFilters: (state, action: PayloadAction<DataTableFilterMeta>) => {
            state.filters = action.payload;
        },
        setPage: (state, action: PayloadAction<TablePageState>) => {
            state.page = action.payload;
        },
        setToEdit: (state, action: PayloadAction<Answer | undefined>) => {
            state.toEdit = action.payload;
        },
        updateItem: (state, action: PayloadAction<Answer>) => {
            state.data = state.data.map((answer) => {
                if (answer.answerId === action.payload.answerId) {
                    return action.payload;
                }
                return answer;
            });
            state.toEdit = undefined;
        },
        setFailed: (state, action: PayloadAction<string[]>) => {
            state.failed = state.failed.concat(action.payload);
        },
        setFilteredAnswers: (state, action: PayloadAction<Answer[]>) => {
            state.filteredAnswers = action.payload;
        },
        clearAnswerData: (state) => {
            state.data = [];
            state.filteredAnswers = [];
            state.selectedInspection = undefined;
            state.error = false;
            state.failed = [];
        },
    },
});

export const {
    setInspections,
    setLoadingInspections,
    setErrorInspections,
    setSelectedInspection,
    setData,
    setLoading,
    setError,
    setGlobalFilterValue,
    setFilters,
    setPage,
    setToEdit,
    updateItem,
    setFailed,
    setFilteredAnswers,
    clearAnswerData,
} = answerSlice.actions;

export default answerSlice.reducer;
